import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { z } from 'zod';

import { useFeatureFlags } from '@/components/providers/FeatureFlagsProvider';
import { useAwsSyncNotification } from '@/hooks/use-aws-sync-notification';
import { useGcpSyncNotification } from '@/hooks/use-gcp-sync-notification';
import { useScanProgress } from '@/hooks/use-scan-progress';
import { gcpServiceAccountQueries } from '@/lib/queries/gcpServiceAccount';
import { awsCredentialsQueries } from '@/lib/queries/integrations/cloud/aws/credentials';
import { awsRoleQueries } from '@/lib/queries/integrations/cloud/aws/roles';
import { k8sClustersQueries } from '@/lib/queries/integrations/containers/k8sClusters';
import { incidentsManagementPagerDutyTokensQueries } from '@/lib/queries/integrations/incidentsManagement/pagerduty/tokens';
import { monitoringDatadogQueries } from '@/lib/queries/integrations/monitoring/datadog';
import { githubConfigQueries } from '@/lib/queries/integrations/scm/github';
import { terraformBackendBucketsQueries } from '@/lib/queries/integrations/terraformBackend/buckets';
import { terraformBackendHcpCloudQueries } from '@/lib/queries/integrations/terraformBackend/hcpCloud';
import { preLoadDefaultProject } from '@/lib/queries/project';

import { Integrations } from './-components/Integrations';
import { TryIt } from './-components/TryIt';
import { getIntegrationData } from './-data';

export const Route = createFileRoute('/_root/integrations/')({
  component: RouteComponent,
  validateSearch: (s: Record<string, unknown>) =>
    z
      .object({
        github: z.string().optional(),
      })
      .parse(s),
  loader: async ({ context }) => {
    const defaultProject = await preLoadDefaultProject({ context });
    context.queryClient.prefetchQuery(
      githubConfigQueries.list(defaultProject.id),
    );
    context.queryClient.prefetchQuery(
      awsCredentialsQueries.list(defaultProject.id),
    );
    context.queryClient.prefetchQuery(awsRoleQueries.list(defaultProject.id));
    context.queryClient.prefetchQuery(
      gcpServiceAccountQueries.list(defaultProject.id),
    );

    return defaultProject;
  },
});

function RouteComponent() {
  const defaultProject = Route.useLoaderData();
  const { FeatureDatadogIntegration, FeatureGCPIntegration, FeaturePagerduty } =
    useFeatureFlags();

  const { data: githubInstall } = useQuery(
    githubConfigQueries.list(defaultProject.id),
  );

  const { data: awsUsers } = useQuery(
    awsCredentialsQueries.list(defaultProject.id),
  );

  const { data: awsRoles } = useQuery(awsRoleQueries.list(defaultProject.id));

  const { data: gcpServiceAccounts } = useQuery(
    gcpServiceAccountQueries.list(defaultProject.id),
  );

  const { data: buckets } = useQuery(
    terraformBackendBucketsQueries.list(defaultProject.id),
  );

  const { data: hcpCloudTokens } = useQuery(
    terraformBackendHcpCloudQueries.list(defaultProject.id),
  );

  const { data: datadogApiCredentials } = useQuery(
    monitoringDatadogQueries.list(defaultProject.id),
  );
  const { data: k8sClusters } = useQuery(
    k8sClustersQueries.list(defaultProject.id),
  );

  const scanProgressState = useScanProgress(defaultProject.id);

  // Monitor AWS credential status changes
  useAwsSyncNotification({ awsUsers, awsRoles });

  // Monitor GCP service account status changes
  useGcpSyncNotification({ gcpServiceAccounts });
  const { data: pagerDutyTokens } = useQuery(
    incidentsManagementPagerDutyTokensQueries.list(defaultProject.id),
  );

  const integrationsData = useMemo(
    () =>
      getIntegrationData({
        awsRoles,
        awsUsers,
        gcpServiceAccounts,
        githubInstall,
        // buckets,
        hcpCloudTokens,
        datadogApiCredentials,
        datadogEnabled: FeatureDatadogIntegration === true,
        gcpEnabled: FeatureGCPIntegration === true,
        k8sClusters,
        awsScanProgress: scanProgressState.aws,
        gcpScanProgress: scanProgressState.gcp,
        pagerDutyTokens,
        pagerdutyEnabled: FeaturePagerduty === true,
      }),
    [
      awsRoles,
      awsUsers,
      gcpServiceAccounts,
      githubInstall,
      // buckets,
      hcpCloudTokens,
      datadogApiCredentials,
      FeatureDatadogIntegration,
      FeatureGCPIntegration,
      k8sClusters,
      scanProgressState,
      pagerDutyTokens,
      FeaturePagerduty,
    ],
  );

  return (
    <>
      <h1 className="text-3xl">
        <FormattedMessage id="integrations.title" />
      </h1>
      <Integrations
        data={integrationsData}
        defaultProjectId={defaultProject.id}
      />
      <TryIt
        awsUsers={awsUsers}
        awsRoles={awsRoles}
        githubInstall={githubInstall}
        buckets={buckets}
        k8sClusters={k8sClusters}
      />
    </>
  );
}
